import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  booleanAttribute
} from '@angular/core';
import { FocusManagementService } from '../services/focus-management.service';

/**
 * Directive that automatically focuses an element when it appears in the DOM.
 * Waits for any ancestor animation to finish before focusing, using the Web
 * Animations API
 */
@Directive({
  selector: '[appAutoFocus]',
  standalone: false
})
export class AutoFocusDirective implements AfterViewInit, OnDestroy {
  private readonly MAX_FOCUS_RETRIES = 10;
  private readonly FOCUS_RETRY_DELAY_MS = 50;

  /**
   * Enables or disables autofocus
   * @default true
   */
  @Input({ transform: booleanAttribute }) appAutoFocus = true;

  /**
   * Whether to skip autofocus on mobile devices.
   * Prevents unwanted keyboard popup for text inputs on phones/tablets.
   * @default false
   */
  @Input() autoFocusSkipMobile = false;

  private destroyed = false;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private focusService: FocusManagementService,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    if (!this.shouldApplyFocus()) return;

    // Skip waiting when user prefers reduced motion — focus immediately (WCAG 2.3.3)
    if (this.focusService.prefersReducedMotion()) {
      this.focusElementWithRetry();
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.waitForAnimations().then(() => {
        if (!this.destroyed && !this.focusService.hasUserInteracted()) {
          this.focusElementWithRetry();
        }
      });
    });
  }

  /**
   * Determines if autofocus should be applied based on configuration and device.
   */
  private shouldApplyFocus(): boolean {
    if (!this.appAutoFocus) return false;

    if (!this.focusService.canApplyProgrammaticFocus()) {
      return false;
    }

    if (this.autoFocusSkipMobile && this.focusService.isMobileDevice()) {
      return false;
    }

    return true;
  }

  /**
   * Waits for any ancestor animation to finish before resolving.
   * Yields one animation frame first so Angular has time to start its animations.
   */
  private waitForAnimations(): Promise<void> {
    return new Promise((resolve) => {
      // Yield one frame so Angular can start animations before we query them
      requestAnimationFrame(() => {
        const animations = this.collectAncestorAnimations();

        if (animations.length === 0) {
          resolve();
          return;
        }

        Promise.all(
          // Catch cancellations (e.g. element removed mid-animation) gracefully
          animations.map((a) => a.finished.catch(() => undefined))
        ).then(() => resolve());
      });
    });
  }

  /**
   * Traverses up the DOM, crossing shadow boundaries, to find any running animations.
   * Returns the animations of the first ancestor that has any.
   */
  private collectAncestorAnimations(): Animation[] {
    let current: Element | null = this.elementRef.nativeElement;

    while (current && current !== document.body) {
      let animations: Animation[] = [];
      try {
        const getAnimationsFn = (current as any).getAnimations;
        if (typeof getAnimationsFn === 'function') {
          animations = getAnimationsFn.call(current) as Animation[];
        }
      } catch {
        // If querying animations fails, fall back to treating as no animations.
        animations = [];
      }
      if (animations.length > 0) {
        return animations;
      }

      // Traverse up, crossing shadow DOM boundaries when reaching a shadow root
      if (current.parentElement) {
        current = current.parentElement;
      } else {
        const root = current.getRootNode();
        current = root instanceof ShadowRoot ? root.host : null;
      }
    }

    return [];
  }

  /**
   * Focuses the element or finds a focusable child element.
   */
  private focusElementWithRetry(attempt = 0): void {
    if (this.destroyed || !this.focusService.canApplyProgrammaticFocus()) {
      return;
    }

    const focused = this.focusService.focusFirstFocusableDeep(
      this.elementRef.nativeElement,
      {
        includeRoot: true
      }
    );

    if (focused || attempt >= this.MAX_FOCUS_RETRIES) {
      return;
    }

    setTimeout(() => {
      this.focusElementWithRetry(attempt + 1);
    }, this.FOCUS_RETRY_DELAY_MS);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }
}
